import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LOGO_VERDE } from '../landing/brand';
import { CALCULADORAS, PRECIOS, SITIO } from '../landing/publicLinks';
import { yaIngresoEnEsteDispositivo } from '../../helpers/ctaPublico';

// Barra de todo el sitio público (landing, precios, calculadoras, blog, foro,
// funcionalidades) con el manual de marca v1.0: logo oficial, Calculadoras ▾,
// Precios, Funcionalidades, Blog, Foro y sesión. Estilos en landing/brand.css.
// En < 1024 px los links pasan a un menú ☰. En la landing arranca transparente
// sobre el hero y se pinta al bajar.

const PRECIO = PRECIOS[0];

const PublicNav: React.FC = () => {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const enLanding = pathname === '/';
  const isActive = (to: string) => pathname === to || pathname.startsWith(to + '/');
  const activa = (to: string) => (isActive(to) ? 'is-active' : undefined);
  const calcActiva = CALCULADORAS.some((c) => isActive(c.to));
  // KAN-289: si este dispositivo ya inició sesión alguna vez, el botón
  // principal es "Ingresar" y no el CTA de registro.
  const yaTieneCuenta = !user && yaIngresoEnEsteDispositivo();

  const closeAll = () => {
    setDrawer(false);
    setCalcOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Cerrar menú y desplegable con Escape o clic afuera.
  useEffect(() => {
    if (!drawer && !calcOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeAll();
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) closeAll();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [drawer, calcOpen]);

  // Cerrar al navegar (cambio de ruta).
  useEffect(() => {
    closeAll();
  }, [pathname]);

  // En la landing, el logo vuelve arriba en vez de re-navegar a la misma ruta.
  const onLogo = (e: React.MouseEvent) => {
    closeAll();
    if (enLanding) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clases = ['lp-scope', 'lp-top', enLanding && 'lp-top--over', scrolled && 'is-scrolled', drawer && 'is-open']
    .filter(Boolean)
    .join(' ');

  return (
    <header ref={ref} className={clases}>
      <div className="lp-wrap">
        <nav className="lp-nav" aria-label="Principal">
          <Link to="/" className="lp-nav__home" onClick={onLogo}>
            <img className="lp-nav__logo" src={LOGO_VERDE} alt="MiCampoDigital" width={154} height={38} />
          </Link>

          <div className="lp-nav__links">
            <div className={`lp-dd ${calcOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className={calcActiva ? 'is-active' : undefined}
                onClick={() => setCalcOpen((o) => !o)}
                aria-expanded={calcOpen}
                aria-haspopup="true"
              >
                Calculadoras <ChevronDown aria-hidden="true" />
              </button>
              {calcOpen && (
                <div className="lp-dd__menu">
                  {CALCULADORAS.map(({ to, label, desc, Icon }) => (
                    <Link key={to} to={to} className={activa(to)}>
                      {Icon && <Icon className="lp-ico" aria-hidden="true" />}
                      <span>
                        <b>{label}</b>
                        {desc}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to={PRECIO.to} className={activa(PRECIO.to)}>
              Precios
            </Link>
            {SITIO.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={activa(to)}
                data-ar-guide={to === '/foro' ? 'nav-foro' : undefined}
              >
                {label}
              </Link>
            ))}
          </div>

          <div className="lp-nav__right">
            {user ? (
              <>
                <button type="button" className="lp-nav__in lp-nav__in--desk" onClick={() => signOut()}>
                  <LogOut size={16} aria-hidden="true" /> Cerrar sesión
                </button>
                <Link className="lp-btn lp-btn--primary lp-btn--sm" to="/dashboard">
                  Mi panel
                </Link>
              </>
            ) : (
              <>
                {/* KAN-295: en la landing "Ingresar" es un botón outline y se
                    ve también en mobile (bajo 1024 px es el único acceso a la
                    sesión fuera del menú ☰) mientras el CTA de registro se
                    aparta porque el hero ya lo trae. En el resto del sitio
                    público la barra no cambia: hay páginas sin CTA de registro
                    en el cuerpo. */}
                {!yaTieneCuenta && (
                  <Link
                    className={enLanding ? 'lp-btn lp-btn--outline lp-btn--sm' : 'lp-nav__in lp-nav__in--desk'}
                    to="/login"
                  >
                    Ingresar
                  </Link>
                )}
                <Link
                  className={`lp-btn lp-btn--primary lp-btn--sm${!yaTieneCuenta && enLanding ? ' lp-nav__signup' : ''}`}
                  to={yaTieneCuenta ? '/login' : '/login?mode=signup'}
                >
                  {yaTieneCuenta ? 'Ingresar' : 'Creá tu cuenta'}
                </Link>
              </>
            )}
            <button
              type="button"
              className="lp-nav__burger"
              onClick={() => {
                setCalcOpen(false);
                setDrawer((o) => !o);
              }}
              aria-expanded={drawer}
              aria-controls="lp-drawer"
              aria-label={drawer ? 'Cerrar menú' : 'Abrir menú'}
            >
              {drawer ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </div>

      {drawer && (
        <div className="lp-drawer" id="lp-drawer">
          <nav className="lp-wrap" aria-label="Menú">
            <small>Herramientas gratis</small>
            {[PRECIO, ...CALCULADORAS].map(({ to, label }) => (
              <Link key={to} to={to} className={activa(to)} onClick={closeAll}>
                {label}
              </Link>
            ))}
            <small>MiCampoDigital</small>
            {SITIO.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={activa(to)}
                onClick={closeAll}
                data-ar-guide={to === '/foro' ? 'nav-foro' : undefined}
              >
                {label}
              </Link>
            ))}
            {user ? (
              <button
                type="button"
                onClick={() => {
                  signOut();
                  closeAll();
                }}
              >
                Cerrar sesión
              </button>
            ) : (
              <Link to="/login" onClick={closeAll}>
                Ingresar
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicNav;
